import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import TabsEmptyState from '../../components/TabsEmptyState';
import { ReactComponent as NoStaffIcon } from '../../../../images/icons/no_staff_icon.svg';
import { Add as AddIcon } from '@mui/icons-material';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { GridColDef } from '@mui/x-data-grid';
import { parseDateToLocal } from '../../../../shared/utils';
import NewModal from '../../../Shared/NewModal';
import StaffModal, { roleValueMap } from './StaffModal';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { TabProps } from '../FacilityDetails';
import {
  addParentFacilityAdmin,
  createFacilityInvite,
  getAdminStaffList,
  getStaffList,
  inviteFacilityAdmin,
  setAddParentFacilityAdminStatus,
  setEditStaffStatus,
  updateCaregiverById,
  updateStaffDetails,
} from '../../../../store/facility/actionCreators';
import { RootState } from '../../../../store';
import {
  dimGrey,
  lightBackground1,
  lightBlack,
  warningBg,
} from '../../../../mainTheme';

const roleColors: Record<string, string> = {
  'Facility Administrator': warningBg,
  'Location Administrator': '#FFF4EB',
  Caregiver: '#F5F9FF',
};

const StaffTab: React.FC<TabProps> = ({
  parentFacilityId,
  locationId,
  locations,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();
  const [editStaff, setEditStaff] = useState<any | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    staffList,
    isLoading,
    addParentFacilityAdminStatus,
    editStaffStatus,
  } = useSelector((state: RootState) => state.facility);

  useEffect(() => {
    if (isFacilityAdmin && locationId) {
      dispatch(getAdminStaffList(locationId));
      return;
    }
    if (parentFacilityId) {
      if (locationId) {
        dispatch(getStaffList(parentFacilityId, locationId));
      } else {
        dispatch(getStaffList(parentFacilityId));
      }
    }
  }, [dispatch, parentFacilityId, locationId]);

  useEffect(() => {
    if (addParentFacilityAdminStatus === true) {
      setStatus('success');
    } else if (addParentFacilityAdminStatus === false) {
      setStatus('error');
    }
  }, [addParentFacilityAdminStatus, parentFacilityId, locationId, dispatch]);

  useEffect(() => {
    if (editStaffStatus === true) {
      setStatus('success');
    } else if (editStaffStatus === false) {
      setStatus('error');
    }
  }, [editStaffStatus]);

  const columns: GridColDef[] = [
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      sortable: true,
      sortComparator: (v1: any, v2: any) => {
        return (v1 ?? '').localeCompare(v2 ?? '');
      },
      renderCell: (params: any) => {
        return (
          <Chip
            label={params.value}
            sx={{
              backgroundColor: roleColors[params.value] || '#F0F0F0',
              color: lightBlack,
              borderRadius: '99px',
              p: 1,
            }}
            size="small"
          />
        );
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (params: any) =>
        `${params.row.first_name} ${params.row.last_name}`,
    },
    { field: 'email', headerName: 'Email', flex: 1 },
    ...(locationId
      ? []
      : [
          {
            field: 'location',
            headerName: 'Location',
            flex: 1,
            renderCell: (params: any) => {
              // This is normalize the location value to always be an array of strings
              let locations: string[] = [];
              if (Array.isArray(params.value)) {
                locations = params.value;
              } else if (
                typeof params.value === 'string' &&
                params.value.trim() !== ''
              ) {
                locations = [params.value];
              }

              if (locations.length === 0) return 'N/A';
              if (locations.length === 1) {
                // Only one location, show as plain text
                return locations[0];
              }
              return (
                <Box
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Tooltip
                    title={
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: lightBackground1,
                          border: '1px solid #D4D4D9',
                          borderRadius: '8px',
                          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                          maxWidth: {
                            xs: '18.75rem',
                            sm: '37.5rem',
                            md: '53.125rem',
                            lg: '62.5rem',
                            xl: '78.125rem',
                          },
                          color: dimGrey,
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.2px',
                          }}
                        >
                          {locations.map((loc, idx) => (
                            <div key={idx}>{loc}</div>
                          ))}
                        </Typography>
                      </Box>
                    }
                    placement="bottom-start"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent',
                          p: 0,
                          maxWidth: 'none',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                      }}
                    >
                      {`${locations.length} locations`}
                    </Box>
                  </Tooltip>
                </Box>
              );
            },
          },
        ]),
    {
      field: 'last_login_at',
      headerName: 'Last login',
      flex: 1,
      renderCell: (params: any) =>
        params.value
          ? parseDateToLocal(params.value, 'yyyy-MM-dd HH:mm:ss')
          : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return (
          <Box
            borderRadius="8px"
            border="1px solid var(--Outline-O5---Separator, #D4D4D9)"
          >
            <Tooltip title="Edit">
              <IconButton onClick={() => setEditStaff(params.row)} size="small">
                <FontAwesomeIcon icon={faPenToSquare} size="sm" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleSubmit = async (values: any) => {
    const { email, roleId, role, first_name, last_name } = values;

    if (isFacilityAdmin) {
      // Use inviteFacilityAdmin for admin roles
      dispatch(
        inviteFacilityAdmin({
          email,
          facility_role_id: roleId,
          first_name,
          last_name,
        }),
      );
    } else if (role === 'facility_admin' && parentFacilityId) {
      dispatch(
        addParentFacilityAdmin(parentFacilityId, {
          first_name,
          last_name,
          email,
          parent_facility_role_id: roleId,
        }),
      );
    } else if (parentFacilityId) {
      const targetLocationId = values.locationId || locationId;
      if (targetLocationId) {
        dispatch(
          createFacilityInvite(targetLocationId, {
            email,
            facility_role_id: roleId,
            first_name,
            last_name,
          }),
        );
      }
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!values.customer_id) return;

    // Find the original staff object
    const original = editStaff;
    // Map original role to internal value for comparison
    const originalRoleValue = roleValueMap[original.role] || '';

    // Build payload with only changed fields
    const payload: any = {};
    if (values.first_name !== original.first_name)
      payload.first_name = values.first_name;
    if (values.last_name !== original.last_name)
      payload.last_name = values.last_name;

    // For isFacilityAdmin, handle role change as facility_role_id
    if (isFacilityAdmin && values.role !== originalRoleValue) {
      payload.facility_role_id = Number(values.roleId);
    } else if (!isFacilityAdmin && values.role !== originalRoleValue) {
      payload.new_role = values.role;
    }

    // If nothing changed, do nothing
    if (Object.keys(payload).length === 0) return;

    if (isFacilityAdmin && locationId) {
      // For admin/location, use updateCaregiverById
      try {
        await dispatch(updateCaregiverById(values.customer_id, payload));
        dispatch(setEditStaffStatus(true));
      } catch (err) {
        dispatch(setEditStaffStatus(false));
      }
    } else {
      dispatch(
        updateStaffDetails(
          values.locationId ?? parentFacilityId,
          values.customer_id,
          payload,
        ),
      );
    }
  };

  const handleStatusClose = () => {
    // If api was a success, refresh staff list when onClose
    if (status === 'success') {
      if (isFacilityAdmin && locationId) {
        dispatch(getAdminStaffList(locationId));
      } else if (parentFacilityId) {
        if (locationId) {
          dispatch(getStaffList(parentFacilityId, locationId));
        } else {
          dispatch(getStaffList(parentFacilityId));
        }
      }
    }
    setStatus('idle');
    setEditStaff(null);
    setShowInvite(false);
    dispatch(setAddParentFacilityAdminStatus(null));
    dispatch(setEditStaffStatus(null));
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {staffList && staffList.length > 0 ? (
        <>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="secondary"
              onClick={() => setShowInvite(true)}
              sx={{
                height: '40px',
              }}
            >
              Invite new staff
            </Button>
          </Box>

          <CustomerCareDTable
            rows={staffList}
            columns={columns}
            pageSize={5}
            defaultSortField="role"
            defaultSortDirection="desc"
            hideViewAllButton
            idField="customer_id"
          />
        </>
      ) : (
        <TabsEmptyState
          icon={<NoStaffIcon />}
          title="There is no staff"
          description="Invite new staff to get started."
          buttonText="Invite new staff"
          onButtonClick={() => setShowInvite(true)}
        />
      )}

      {editStaff && (
        <NewModal showCloseX onClose={handleStatusClose} maxWidth="xs">
          <StaffModal
            staff={editStaff}
            onClose={handleStatusClose}
            mode="edit"
            onSubmit={handleEditSubmit}
            status={status}
            locations={locations}
            isFacilityAdmin={isFacilityAdmin}
          />
        </NewModal>
      )}
      {showInvite && (
        <NewModal showCloseX onClose={handleStatusClose} maxWidth="xs">
          <StaffModal
            onClose={handleStatusClose}
            mode="invite"
            onSubmit={handleSubmit}
            status={status}
            locations={locations}
            currentLocationId={locationId}
            isFacilityAdmin={isFacilityAdmin}
          />
        </NewModal>
      )}
    </Box>
  );
};

export default StaffTab;
